import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { ApiError, httpSearchAdapter } from '@/lib/http';
import type { SearchHit } from '@/lib/search/search-service';

export const SEARCH_DEBOUNCE_MS = 150;

export type DocumentSearchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; hits: SearchHit[] }
  | { status: 'error' };

type Answer = { query: string; hits: SearchHit[] } | { query: string; failed: true };

function reportSearchFailure(error: unknown): void {
  if (error instanceof ApiError && error.status < 500) {
    console.warn('document search refused', error);
    return;
  }
  if (error instanceof TypeError) {
    console.warn('document search failed', error);
    return;
  }
  Sentry.captureException(error);
  console.error('document search failed', error);
}

/** Server search for documents, debounced; an answer to an older query is dropped. */
export function useDocumentSearch(query: string): DocumentSearchState {
  const needle = query.trim();
  const [answer, setAnswer] = useState<Answer | null>(null);

  useEffect(() => {
    if (needle === '') return;
    let current = true;
    const timer = setTimeout(() => {
      httpSearchAdapter.searchDocuments(needle).then(
        (hits) => {
          if (!current) return;
          setAnswer({
            query: needle,
            hits: hits.filter((hit) => hit.kind === 'document' && !hit.isArchived),
          });
        },
        (error: unknown) => {
          if (!current) return;
          reportSearchFailure(error);
          setAnswer({ query: needle, failed: true });
        },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [needle]);

  if (needle === '') return { status: 'idle' };
  if (answer === null || answer.query !== needle) return { status: 'loading' };
  if ('failed' in answer) return { status: 'error' };
  return { status: 'ready', hits: answer.hits };
}
